import React, { useState, useRef, useEffect } from 'react';
import { calculateAge } from '../../utils/index.ts';
import { capitalReturnedWords, capitalAccessPeriodFor, residualQuestion, residualTooltip } from '../../components/strategy/annuityWords.ts';
import AnnuityShapeChoice, { guaranteedShapeAvailable } from '../../components/strategy/AnnuityShapeChoice.tsx';
import { carePlusSplitFor } from '../../../../shared/src/calculation/carePlusSplit.ts';
import { AnnuityExplainerModal } from '../../components/strategy/AnnuityTerms.tsx';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { createPortal } from 'react-dom';
import { ShieldCheck } from 'lucide-react';
import { HelpCircle } from 'lucide-react';
import { lookupGlossaryShort } from '../../constants/awiseKnowledgeBase.ts';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { IncomeOnlyTick, isIncomeOnlyAnnuity } from '../../components/strategy/IncomeOnlyTick.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { annuityCeilingOf, annuityCeilingAmount, DEFAULT_ANNUITY_SHARE_PCT } from '../../../../shared/src/calculation/annuityPreference.ts';
import { householdVoice } from '../../utils/householdWords.ts';

/*
 * ABOVE THE CLIENT'S CEILING, A CAUTION — NOT A BLOCK (owner, 21 Oct 2026 — the guaranteed income review M3).
 * A plan built by hand can put in more than the share the client said at most (annuityCeilingOf: 25% of the
 * money that could be invested unless they answered otherwise). The tested plans never do; a hand-built one
 * may, for a reason, so it is said plainly beside the amount rather than refused.
 */
export function annuityCeilingCautionWords(
    factFind: DraftFactFind | null | undefined,
    amount: number,
    investable: number,
    personName?: string | null,
): string | null {
    const inv = Math.max(0, Number(investable) || 0);
    const a = Math.max(0, Number(amount) || 0);
    if (!(inv > 0) || !(a > 0)) return null;
    const ceiling = annuityCeilingOf(factFind as never);
    if (a <= annuityCeilingAmount(ceiling, inv) + 1) return null;
    const v = householdVoice(factFind);
    const has = personName ? `${personName} has` : v.you ? 'you have' : `${v.names} ${v.have}`;
    const share = Math.round((a / inv) * 100);
    const suggest = ceiling.amount != null
        ? `${personName || (v.you ? 'You' : v.Names)} said no more than ${formatMoney(ceiling.amount)}`
        : ceiling.answered
            ? `${personName || (v.you ? 'You' : v.Names)} said no more than ${ceiling.pct}%`
            : `We suggest no more than ${ceiling.pct ?? DEFAULT_ANNUITY_SHARE_PCT}%`;
    return `This puts ${share}% of what ${has} into a guaranteed income. ${suggest}, so most of the money stays within reach.`;
}

const formatMoney = (n: number) => `$${Math.round(n).toLocaleString('en-AU')}`;

export const AnnuityCeilingCaution: React.FC<{ factFind: DraftFactFind | null | undefined; amount: number; investable: number; personName?: string | null }> = ({ factFind, amount, investable, personName }) => {
    const words = annuityCeilingCautionWords(factFind, amount, investable, personName);
    if (!words) return null;
    return (
        <p className="rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2 text-[12px] leading-relaxed text-slate-700" data-testid="annuity-ceiling-caution">
            {words}
        </p>
    );
};

interface InfoIconProps {
    text?: string | null;
    children?: React.ReactNode;
}

const InfoIcon: React.FC<InfoIconProps> = ({ text, children }) => {
    const [show, setShow] = useState(false);
    const iconRef = useRef<HTMLSpanElement>(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    useEffect(() => {
        if (show && iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            setCoords({
                top: rect.top - 40,
                left: rect.left + rect.width / 2
            });
        }
    }, [show]);
    return (
        <span ref={iconRef} className="relative inline-block ml-1 align-middle" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
            <HelpCircle size={15} className="text-slate-400 cursor-pointer" />
            {show && typeof window !== 'undefined' && window.document &&
                createPortal(
                    <div style={{
                        position: 'fixed',
                        top: coords.top,
                        left: coords.left,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 10001,
                        width: 'max-content',
                        maxWidth: '300px',
                        pointerEvents: 'auto'
                    }}
                        className="p-3 bg-slate-900 text-white text-xs leading-relaxed rounded-lg shadow-lg text-left"
                    >
                        {typeof text === 'string' ? text : children}
                    </div>,
                    document.body
                )
            }
        </span>
    );
};

interface AnnuityTypeInfo {
    word: string;
    oneLiner: string;
    get: string[];
    giveUp: string[];
    pension: string;
    goodFit: string[];
    notWorth: string[];
}

// Tightened, lay-friendly, de-duplicated content (one fact stated once; precision in tooltips).
const typeInfo: Record<'lifetime' | 'fixedTerm', AnnuityTypeInfo> = {
    lifetime: {
        word: 'income-for-life',
        oneLiner: 'Pays you a guaranteed income for the rest of your life. Because Centrelink counts only part of it, it can also lift your Age Pension.',
        get: ['Income you can’t outlive', 'A higher Age Pension (less is counted)', 'No worry about share markets'],
        giveUp: ['Getting the lump sum back', 'Growth on that money', 'Flexibility for a big future cost'],
        pension: 'Centrelink counts 60% of what you put in as an asset for the first five years, or until age 85 if that is later, and 30% after that. It counts 60% of each payment as income.',
        goodFit: ['You’re near or just over the pension assets limit', 'You want a guaranteed income you can rely on', 'You worry about running your savings down'],
        notWorth: ['You’re already on the full Age Pension', 'You’re well above the limit as a self-funded retiree', 'You may need a large lump sum later (like a care room deposit)']
    },
    fixedTerm: {
        word: 'set-term',
        oneLiner: 'Pays you a guaranteed income for a set number of years you choose. At the end, you get back the amount you chose to keep.',
        get: ['Predictable income for a set period', 'You choose how much comes back at the end', 'Useful if you plan to sell an asset later'],
        giveUp: ['Income stops when the term ends', 'No growth on that money', 'Less of the pension concession a lifetime annuity gets'],
        pension: 'Centrelink counts the actual value, which reduces over the term if you take back less than 100%. For terms over 5 years only the income portion is counted; for 5 years or less, deeming rates apply.',
        goodFit: ['You have a timeframe in mind (such as waiting to sell your home)', 'You want your capital returned at the end', 'You want assessed assets to reduce over time'],
        notWorth: ['You want income protection for life', 'You’re well below the assets limit', 'You choose 100% back (the full value is counted the whole term)']
    }
};

const providerLinks: { name: string; url: string }[] = [
    { name: 'Challenger', url: 'https://www.challenger.com.au' },
    { name: 'Generation Life', url: 'https://www.genlife.com.au' }
];

/** A candidate asset/pool an annuity purchase can be funded from. `ownership` stays a generic
 * `string` (not the usual `'client'|'partner'|'joint'` literal union) since some callers (e.g.
 * RVOtherStrategiesSection.tsx, StrategiesSection.tsx) build these lists with plain-string ownership. */
/*
 * A NEW ANNUITY STARTS WITH THE CAPITAL PRESERVED.
 *
 * OWNER-ASKED, 2026-09-02: "is there a reason the default is 50% and not 100% RCV?" There was not.
 * 50% had been the starting point since the first commit, with no comment and no consistency — the
 * Retirement Village started at nil, Aged Care and Support at Home at 50%. It was a midpoint, not a
 * decision, and it is not even a shape most providers sell: real lifetime annuities are quoted at
 * the ends, which is why every engine models 0% and 100% and nothing between.
 *
 * 100% is the right place to open, because the means test does not care and the client does:
 *
 *   THE PENSION IS IDENTICAL EITHER WAY. Centrelink counts 60% of the purchase price whatever the
 *   residual, so defaulting to full capital costs nothing in means-test benefit.
 *   IT IS THE LEAST IRREVERSIBLE. At nil residual the capital is gone for good; at 100% it comes
 *   back to the estate. A default should never be the option that quietly commits a client's money
 *   permanently — that has to be a choice they make, and the income difference makes it visible.
 *
 * One line to change if a different opening is wanted; the client can still set anything.
 */
export const DEFAULT_NEW_ANNUITY_RESIDUAL = 1;

export interface AnnuityFundingSource {
    id: string;
    type?: string;
    assetId?: string | number | null;
    label?: string;
    ownership?: string;
    [key: string]: unknown;
}

/** Adviser-facing annuity configuration state — shape is owned by this component; consumers (e.g.
 * ProceedsDeploymentFields.tsx) adapt their own state into/out of this bag. */
export interface AnnuityDetails {
    /** `guaranteed` is the annuity-plus-life-policy shape: the full amount to the estate, always. */
    annuityType?: 'lifetime' | 'fixedTerm' | 'guaranteed';
    purchaseAmount?: number;
    residualCapital?: number;
    termYears?: number | null;
    /** Lifetime only: years before the payments start (a deferred lifetime annuity). */
    deferralYears?: number | null;
    indexation?: boolean;
    reversionary?: boolean;
    owner?: 'client' | 'partner' | 'joint';
    partnerAge?: number | null;
    fundingSource?: AnnuityFundingSource | null;
    incomeOverride?: number | null;
    assessableIncomeOverride?: number | null;
    assessableAssetOverride?: number | null;
    [key: string]: unknown;
}

/** Calc-engine annuity outcome bag (buildAnnuityImpact / summariseAnnuityImpact result) — free-form. */
export interface AnnuitySummary {
    annualPayment?: number;
    assessableAsset?: number | null;
    assessableIncome?: number;
    [key: string]: unknown;
}

export interface AnnuityStrategyConfiguratorProps {
    factFind: DraftFactFind;
    annuityDetails: AnnuityDetails | null | undefined;
    setAnnuityDetails: (updater: AnnuityDetails | ((prev: AnnuityDetails) => AnnuityDetails)) => void;
    annuitySummary?: AnnuitySummary | null;
    annuityWarnings?: string[];
    fundingSources?: AnnuityFundingSource[];
    formatCurrency: (value: number | string | null | undefined) => string;
    clientAge?: number | null;
    partnerAge?: number | null;
    hideAmount?: boolean;
    /**
     * Writes the client's tick that nothing comes back from an income-only annuity (goals.incomeOnlyUnderstood).
     * Given by a module whose hand-built plan is gated on it; the tick shows under "the highest income".
     */
    onIncomeOnlyUnderstoodChange?: (confirmed: boolean, amount: number) => void;
    /** The tick as the module holds it, where `factFind` here is a simulated copy that may lag the client's file. */
    incomeOnlyUnderstood?: boolean;
    /**
     * Optional (additive, 21 Oct 2026): the money a guaranteed income could come from, so an amount above the
     * client's ceiling is cautioned beside it (AnnuityCeilingCaution). `ceilingFactFind` is the client's own
     * file when `factFind` here is a simulated copy. Not given: no caution.
     */
    ceilingInvestable?: number;
    ceilingFactFind?: DraftFactFind | null;
}

const AnnuityStrategyConfigurator: React.FC<AnnuityStrategyConfiguratorProps> = ({
    factFind,
    annuityDetails,
    setAnnuityDetails,
    annuitySummary,
    annuityWarnings,
    fundingSources,
    formatCurrency,
    clientAge,
    partnerAge,
    hideAmount = false,
    onIncomeOnlyUnderstoodChange,
    incomeOnlyUnderstood,
    ceilingInvestable,
    ceilingFactFind,
}) => {
    const isCouple = factFind?.adviceFor === 'couple';
    const availableOptions: AnnuityFundingSource[] = (fundingSources && fundingSources.length > 0)
        ? fundingSources
        : [{ id: 'bank-default', type: 'bank', assetId: null, label: 'Bank accounts / cash', ownership: isCouple ? 'joint' : 'client' }];

    // Hooks run on every render, so this sits ABOVE the early return below (React's rules of hooks).
    const [showAnnuityExplainer, setShowAnnuityExplainer] = useState(false);
    // Opened from the full-guarantee card, which cannot be modelled until a quote is entered.
    const [quoteOpen, setQuoteOpen] = useState(false);
    /*
     * THE GUARANTEE IS THE ANNUITY TYPE, NOT A SCREEN FLAG (27 Sep 2026, owner-caught). This was
     * `useState(false)`: selecting the third card highlighted it, opened the quote panel, and
     * changed nothing the engine could see, so the figures beneath the card belonged to a different
     * shape and nothing reached the projection. It now sets `annuityType: 'guaranteed'` on the
     * record, which `buildAnnuityImpact` prices off the published table and splits between the
     * annuity and the life policy from Challenger's own calculator. Choosing one of the plain cards
     * sets it back to a lifetime annuity with whatever residual the client had.
     */
    const wantsGuarantee = annuityDetails?.annuityType === 'guaranteed';
    const setWantsGuarantee = (wants: boolean) => setAnnuityDetails(prev => ({
        ...prev,
        annuityType: wants ? 'guaranteed' : 'lifetime',
        residualCapital: wants ? 1 : (prev.residualCapital ?? DEFAULT_NEW_ANNUITY_RESIDUAL),
        termYears: null,
        deferralYears: wants ? 0 : prev.deferralYears,
    }));
    /** The provider block, so the estimate badge can take the client straight to it. */
    const quoteRef = useRef<HTMLDivElement>(null);
    /** A quote already typed keeps the block open - a client's own figures must never be hidden. */
    const hasQuote = annuityDetails?.incomeOverride != null
        || annuityDetails?.assessableIncomeOverride != null
        || annuityDetails?.assessableAssetOverride != null;

    if (!annuityDetails) {
        return <div className="text-sm text-slate-600 p-4">Annuity configuration not initialized.</div>;
    }

    const type = annuityDetails.annuityType === 'fixedTerm' ? 'fixedTerm' : 'lifetime';
    const info = typeInfo[type];
    const isGuaranteedShape = annuityDetails.annuityType === 'guaranteed';
    const currentFundingId = annuityDetails.fundingSource?.id || availableOptions[0].id;
    const residualPercent = Math.round((annuityDetails.residualCapital || 0) * 100);

    // Adviser-optimal structuring: when a couple wants continuation, run it reversionary with the
    // OLDER partner as primary owner (the survivor inherits the earlier drop to 30% asset assessment).
    const olderOwner = (partnerAge != null && partnerAge > (clientAge ?? 0)) ? 'partner' : 'client';
    // Continuation comes from a reversionary nomination OR joint ownership (joint pays the survivor without a nomination).
    const continuation = (annuityDetails.reversionary || annuityDetails.owner === 'joint') ? 'continue' : 'highest';
    const recommendedOwner = olderOwner;

    // Funding super/ABP money can only be held in one name (super isn't jointly owned) → forces a reversionary structure.
    const isSuperSource = (() => {
        const opt = availableOptions.find(o => o.id === currentFundingId) || availableOptions[0];
        return /super|account-based pension|allocated pension|abp/i.test(`${opt?.label || ''} ${opt?.type || ''}`);
    })();

    const clientName = factFind?.client?.firstName || 'You';
    const partnerName = factFind?.partner?.firstName || 'your partner';
    const rawOwner = annuityDetails.owner || recommendedOwner;
    // Joint isn't valid with super money, or with "highest income now" (which stops on death).
    const ownerKey = (rawOwner === 'joint' && (isSuperSource || continuation === 'highest')) ? recommendedOwner : rawOwner;
    const ownerName = ownerKey === 'partner' ? partnerName : clientName;
    const otherName = ownerKey === 'partner' ? clientName : partnerName;
    const ownershipShort = lookupGlossaryShort('annuity ownership');

    const handleFundingSourceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const option = availableOptions.find(item => item.id === event.target.value);
        if (!option) return;
        const optionIsSuper = /super|account-based pension|allocated pension|abp/i.test(`${option.label || ''} ${option.type || ''}`);
        setAnnuityDetails(prev => {
            const next: AnnuityDetails = { ...prev, fundingSource: option };
            // Super can't be jointly owned — move to a single-owner reversionary structure.
            if (optionIsSuper && prev.owner === 'joint') {
                next.owner = recommendedOwner;
                next.reversionary = true;
                next.partnerAge = partnerAge ?? prev.partnerAge ?? null;
            }
            return next;
        });
    };

    const handleTypeChange = (nextType: 'lifetime' | 'fixedTerm') => {
        setAnnuityDetails(prev => ({
            ...prev,
            // A guaranteed shape IS income for life; clicking that card again keeps the guarantee.
            annuityType: nextType === 'lifetime' && prev.annuityType === 'guaranteed' ? 'guaranteed' : nextType,
            residualCapital: nextType === 'lifetime'
                ? (prev.residualCapital ?? DEFAULT_NEW_ANNUITY_RESIDUAL)
                : (prev.residualCapital ?? 0),
            termYears: nextType === 'fixedTerm' ? (prev.termYears || 10) : null
        }));
    };

    const handleNumberChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(event.target.value);
        setAnnuityDetails(prev => ({ ...prev, [field]: Number.isNaN(value) ? 0 : value }));
    };

    const handleResidualChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(event.target.value);
        const normalised = Number.isNaN(value) ? 0 : Math.min(Math.max(value / 100, 0), 1);
        setAnnuityDetails(prev => ({ ...prev, residualCapital: normalised }));
    };

    const setContinuation = (mode: 'continue' | 'highest') => {
        if (mode === 'continue') {
            setAnnuityDetails(prev => {
                const keepJoint = prev.owner === 'joint' && !isSuperSource;
                return {
                    ...prev,
                    reversionary: keepJoint ? false : true,
                    partnerAge: partnerAge ?? prev.partnerAge ?? null,
                    owner: keepJoint ? 'joint' : ((prev.owner && prev.owner !== 'joint') ? prev.owner : recommendedOwner)
                };
            });
        } else {
            setAnnuityDetails(prev => ({
                ...prev,
                reversionary: false,
                partnerAge: null,
                owner: (prev.owner && prev.owner !== 'joint') ? prev.owner : recommendedOwner
            }));
        }
    };

    const setOwner = (key: 'client' | 'partner' | 'joint') => {
        if (key === 'joint') {
            setAnnuityDetails(prev => ({ ...prev, owner: 'joint', reversionary: false, partnerAge: partnerAge ?? prev.partnerAge ?? null }));
        } else {
            setAnnuityDetails(prev => ({
                ...prev,
                owner: key,
                reversionary: continuation === 'continue',
                partnerAge: continuation === 'continue' ? (partnerAge ?? prev.partnerAge ?? null) : null
            }));
        }
    };

    // Outcome figures (honest: no benefit shown when nothing is freed from the asset test).
    const purchaseAmount = parseFloat(String(annuityDetails.purchaseAmount)) || 0;
    const assessableAsset = annuitySummary?.assessableAsset ?? null;
    /*
     * The two halves of a guaranteed shape, for the screen. Off the engine's summary where the
     * parent has run it; reconstructed from the same table otherwise, so the note under the card
     * never waits on a round trip to say where the money went.
     */
    const guaranteedSlices = (() => {
        if (!isGuaranteedShape || !(purchaseAmount > 0)) return null;
        const fromSummary = annuitySummary as { annuitySlice?: number; insuranceSlice?: number; splitClamped?: boolean } | null | undefined;
        if (fromSummary?.annuitySlice != null && fromSummary?.insuranceSlice != null) {
            return { annuity: fromSummary.annuitySlice, insurance: fromSummary.insuranceSlice, clamped: !!fromSummary.splitClamped };
        }
        const ownerForSplit = annuityDetails.owner === 'partner' ? 'partner' : 'client';
        const dob = ownerForSplit === 'partner' ? factFind?.partner?.dob : factFind?.client?.dob;
        const sex = ownerForSplit === 'partner' ? factFind?.partner?.sex : factFind?.client?.sex;
        const age = dob ? calculateAge(dob) : null;
        if (age == null) return null;
        const split = carePlusSplitFor(age, sex === 'male' ? 'male' : sex === 'female' ? 'female' : null);
        return { annuity: purchaseAmount * split.annuity, insurance: purchaseAmount * split.insurance, clamped: split.clamped };
    })();
    const assetBenefit = (annuitySummary && purchaseAmount > 0 && assessableAsset != null)
        ? Math.max(0, purchaseAmount - assessableAsset)
        : 0;

    return (
        <div className="mt-6 border-t border-slate-200 pt-6 space-y-4">
            {/*
              * WHAT AN ANNUITY IS, before asking anyone to structure one (owner, 2026-09-01: "an
              * annuity will be a foreign concept so we will need to have some kind of hyperlink
              * whereby the client can select it and get a very clear explanation").
              *
              * This card asks the client to choose a type, a residual, an owner and whether it is
              * reversionary — four decisions about a product most people are meeting for the first
              * time. The explainer sits at the top because that is where the questions start. Shared
              * with the Suggestion Engine cards, so both routes explain the same product.
              */}
            <button
                type="button"
                onClick={() => setShowAnnuityExplainer(true)}
                className="text-[12px] font-semibold text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700"
            >
                What is an annuity, and what are the risks?
            </button>
            <AnnuityExplainerModal open={showAnnuityExplainer} onClose={() => setShowAnnuityExplainer(false)} />

            {/* 1. Choose the type */}
            <div>
                <p className="text-[12px] font-semibold text-slate-700 mb-1.5">What kind of income do you want?</p>
                <div className="grid grid-cols-2 gap-2">
                    {([
                        { id: 'lifetime', title: 'Income for life', sub: 'Paid until you (or your partner) die' },
                        { id: 'fixedTerm', title: 'For a set number of years', sub: 'Paid for a term you choose' }
                    ] as { id: 'lifetime' | 'fixedTerm'; title: string; sub: string }[]).map(opt => {
                        const active = type === opt.id;
                        return (
                            <button key={opt.id} type="button" onClick={() => handleTypeChange(opt.id)}
                                className={`text-left rounded-xl p-2.5 bg-white transition-colors ${active ? 'border-2 border-blue-600' : 'border border-slate-300 hover:border-blue-400'}`}>
                                <span className="block text-[13px] font-semibold text-slate-900">{opt.title}</span>
                                <span className="block text-[11px] text-slate-500">{opt.sub}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* 2. Education — one collapsed panel, not two stacked ones */}
            <details className="group rounded-xl border border-slate-200 bg-white">
                <summary className="flex items-center justify-between gap-2 px-3 py-2.5 cursor-pointer list-none">
                    <span className="flex items-center gap-2 text-[12px] font-semibold text-slate-800">
                        <span className="grid place-items-center w-6 h-6 rounded-md bg-slate-100 text-slate-600 text-xs shrink-0">?</span>
                        <span>How an {info.word} annuity works, and whether it suits you</span>
                    </span>
                    <svg className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path d="M5.2 7.5 10 12l4.8-4.5" /></svg>
                </summary>
                <div className="px-3 pb-3 pt-1 border-t border-slate-200 text-[11.5px] text-slate-600 space-y-2.5">
                    <p>{info.oneLiner}</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <p className="text-[11px] font-semibold text-emerald-700 mb-0.5">What you get</p>
                            <ul className="space-y-0.5 list-disc list-inside marker:text-slate-300">
                                {info.get.map(item => <li key={item}>{item}</li>)}
                            </ul>
                        </div>
                        <div>
                            <p className="text-[11px] font-semibold text-amber-700 mb-0.5">What you give up</p>
                            <ul className="space-y-0.5 list-disc list-inside marker:text-slate-300">
                                {info.giveUp.map(item => <li key={item}>{item}</li>)}
                            </ul>
                        </div>
                    </div>
                    <details className="group/inner">
                        <summary className="text-[11.5px] font-medium text-blue-700 cursor-pointer list-none">How it affects your pension</summary>
                        <p className="mt-1 rounded-lg bg-white border border-slate-200 border-l-4 border-l-blue-500 px-3 py-2">{info.pension}</p>
                    </details>

                    {/*
                      * "Is an annuity right for you?" used to be a second collapsed panel of its own,
                      * directly under this one, with the "what is an annuity" modal link above them
                      * both. Three ways in to the same education, stacked, is a good part of why the
                      * owner found the page daunting. It is the same content, one row down.
                      */}
                    <div className="rounded-lg bg-white border border-slate-200 border-l-4 border-l-emerald-500 px-2.5 py-2">
                        <p className="text-[11px] font-semibold text-slate-800 mb-1">Often a good fit if…</p>
                        <ul className="space-y-0.5 list-disc list-inside marker:text-slate-300">{info.goodFit.map(i => <li key={i}>{i}</li>)}</ul>
                    </div>
                    <div className="rounded-lg bg-white border border-slate-200 border-l-4 border-l-amber-500 px-2.5 py-2">
                        <p className="text-[11px] font-semibold text-slate-800 mb-1">Probably not worth it if…</p>
                        <ul className="space-y-0.5 list-disc list-inside marker:text-slate-300">{info.notWorth.map(i => <li key={i}>{i}</li>)}</ul>
                    </div>
                    <p className="text-[11px] text-slate-500">A common guide is to keep an annuity to about a third of your savings, so the rest stays flexible.</p>
                </div>
            </details>

            {/* 3. Set it up */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                <p className="text-[12px] font-semibold text-slate-700">Set it up</p>
                {!hideAmount && (
                    <CurrencyInput
                        label="How much do you want to put in?"
                        value={annuityDetails.purchaseAmount ?? ''}
                        onChange={(v) => setAnnuityDetails(prev => ({ ...prev, purchaseAmount: v ?? 0 }))}
                        placeholder="$0"
                        className="bg-white rounded-lg"
                    />
                )}
                {ceilingInvestable != null && (
                    <AnnuityCeilingCaution factFind={ceilingFactFind ?? factFind} amount={purchaseAmount} investable={ceilingInvestable} />
                )}
                <div>
                    <label className="flex items-center gap-1 text-[12px] font-medium text-slate-700 mb-1">Where will the money come from?
                        <InfoIcon text="You’re moving an existing asset into guaranteed income. We list your savings and what’s available in each." />
                    </label>
                    <select value={currentFundingId} onChange={handleFundingSourceChange}
                        className="w-full px-2 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                        {availableOptions.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                    <p className="text-[11px] text-slate-500 mt-1">Moving money out of cash or shares is what can lift your pension.</p>
                </div>
                {type === 'fixedTerm' && (
                    <div>
                        <label className="block text-[12px] font-medium text-slate-700 mb-1">For how many years?</label>
                        <input type="number" min="1" value={annuityDetails.termYears || ''} onChange={handleNumberChange('termYears')}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
                        <p className="text-[11px] text-slate-500 mt-1">
                            {Number(annuityDetails.termYears) > 0 && Number(annuityDetails.termYears) <= 5
                                ? 'Five years or less: Centrelink treats it like a term deposit — the value is deemed and the payments are not counted.'
                                : 'More than five years: the value counted falls as capital comes back, and only the income portion of each payment counts.'}
                        </p>
                    </div>
                )}
                {/*
                  * DEFERRAL AND INDEXATION FOLD AWAY. Both are real options and both are rare: most
                  * clients take the payments now and take them level. Left open they added two rows
                  * to "Set it up" for everyone, which was part of what made the form feel long.
                  * Opened automatically whenever either has been set, so a chosen option is never
                  * hidden from the person who chose it.
                  */}
                <details open={Number(annuityDetails.deferralYears) > 0 || !!annuityDetails.indexation}>
                    <summary className="text-[11.5px] font-medium text-blue-700 cursor-pointer list-none">More options: start later, or rise with prices</summary>
                    <div className="mt-2 space-y-3">
                {type === 'lifetime' && (
                    <div>
                        <label className="block text-[12px] font-medium text-slate-700 mb-1">
                            Start the payments later? (years to wait)
                            <InfoIcon text="A deferred lifetime annuity: you buy it now and the payments start after the number of years you choose. It pays more when it starts, because the provider holds the money longer. Centrelink counts 60% of the purchase price from the day you buy, and none of the income until the payments begin. Enter 0 for payments that start straight away." />
                        </label>
                        <input type="number" min="0" max="20" value={annuityDetails.deferralYears || ''} placeholder="0"
                            onChange={handleNumberChange('deferralYears')}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
                        {Number(annuityDetails.deferralYears) > 0 && (
                            <p className="text-[11px] text-slate-500 mt-1">No income for {Number(annuityDetails.deferralYears)} years, then a higher payment for life. Our figure is indicative — a provider&apos;s quote will differ.</p>
                        )}
                    </div>
                )}
                <label className="flex items-start gap-2 text-[12px] text-slate-700">
                    <input type="checkbox" className="mt-0.5" checked={!!annuityDetails.indexation}
                        onChange={(e) => setAnnuityDetails(prev => ({ ...prev, indexation: e.target.checked }))} />
                    <span>Increase the payments each year to keep up with prices
                        <InfoIcon text="Payments rise over time so they hold their value. They start lower and grow later." /></span>
                </label>
                    </div>
                </details>
            </div>

            {/*
              * 4. TWO DIFFERENT QUESTIONS, TWO SECTIONS.
              *
              * Owner on the live screen, 25 Sep 2026: "the form is huge and somewhat repetitive — 'if
              * one of you passes away' but then we have 'what matters most to you'." He was right,
              * and the cause was one heading over two unrelated questions. "If one of you passes
              * away" covered whether the INCOME keeps going to the survivor, and then the capital
              * question sat underneath it, so the two read as the same question asked twice. They
              * are now separate sections, each headed with the question it actually asks.
              */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                <div>
                    <p className="text-[12px] font-semibold text-slate-700">What happens when you die</p>
                    <p className="text-[11.5px] leading-relaxed text-slate-600 mt-0.5">
                        {isCouple
                            ? 'Two separate things, and you choose each one. The payments either stop or keep going to the survivor. And a lump sum either comes back to your family or it doesn’t — where the payments continue, that lump sum waits until you have both died.'
                            : 'The payments are for your lifetime, so they stop. What can still come back is a lump sum — that is the choice below.'}
                    </p>
                </div>

                {isCouple && (
                    <p className="text-[11.5px] font-semibold text-slate-800">1. Do the payments keep going to {otherName}?</p>
                )}

                {isCouple && (
                    <>
                        <div className="space-y-2">
                            <label className={`flex items-start gap-2 rounded-lg bg-white p-2.5 cursor-pointer ${continuation === 'continue' ? 'border-2 border-blue-600' : 'border border-slate-300'}`}>
                                <input type="radio" name="annuity-continuation" className="mt-0.5" checked={continuation === 'continue'} onChange={() => setContinuation('continue')} />
                                <span className="text-[12px] text-slate-700">
                                    <span className="font-semibold text-slate-900">Keep paying the survivor for life</span>
                                    <span className="ml-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Recommended</span>
                                    <span className="block text-[11px] text-slate-500">A little less income now, but it’s secure for whoever lives longer.</span>
                                </span>
                            </label>
                            <label className={`flex items-start gap-2 rounded-lg bg-white p-2.5 cursor-pointer ${continuation === 'highest' ? 'border-2 border-blue-600' : 'border border-slate-300'}`}>
                                <input type="radio" name="annuity-continuation" className="mt-0.5" checked={continuation === 'highest'} onChange={() => setContinuation('highest')} />
                                <span className="text-[12px] text-slate-700">
                                    <span className="font-semibold text-slate-900">Pay the highest income now</span>
                                    <span className="block text-[11px] text-slate-500">More income while you’re alive, but payments stop on death.</span>
                                </span>
                            </label>
                        </div>

                        <div>
                            <label className="flex items-center gap-1 text-[12px] font-medium text-slate-700 mb-1">Whose name is it in?
                                <InfoIcon text={ownershipShort} />
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {(continuation === 'continue' && !isSuperSource
                                    ? (['client', 'partner', 'joint'] as const)
                                    : (['client', 'partner'] as const)).map(key => {
                                    const label = key === 'joint' ? 'Joint' : (key === 'partner' ? partnerName : clientName);
                                    const active = ownerKey === key;
                                    return (
                                        <button key={key} type="button" onClick={() => setOwner(key)}
                                            className={`px-3 py-1.5 text-[12px] rounded-lg bg-white ${active ? 'border-2 border-blue-600 font-semibold' : 'border border-slate-300'}`}>
                                            {label}{key === recommendedOwner ? <span className="ml-1 text-[10px] font-semibold text-emerald-700">· suggested</span> : null}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-slate-600 mt-1.5">
                                {continuation === 'highest'
                                    ? `In ${ownerName}’s name — payments stop on death.`
                                    : (ownerKey === 'joint'
                                        ? 'Owned jointly — the income continues to whoever lives longer.'
                                        : `In ${ownerName}’s name, continuing to ${otherName} if ${ownerName} passes first (a reversionary annuity).${isSuperSource ? ' Super can only be held in one name, which is why.' : ''}`)}
                            </p>
                            {/*
                              * This note belongs beside the ownership buttons it explains. It had
                              * drifted down into the capital section, where it read as a fourth
                              * unrelated paragraph.
                              */}
                            {/*
                              * ONE SHORT LINE. This block had stacked three paragraphs of grey small
                              * print under the ownership buttons. The super caveat is folded into the
                              * arrangement line above, and the reasoning stays behind the jargon term
                              * where a client who wants it can find it and everyone else is not made
                              * to read it.
                              */}
                            <p className="text-[11px] text-slate-600 mt-1.5">
                                It makes no difference to your pension or your care fees &mdash; you&rsquo;re assessed as a couple
                                either way &mdash; so we&rsquo;ve suggested the one that works best{' '}
                                <JargonTerm term="(why this one?)">
                                    {`The counted share drops from 60% to 30% once the owner has held it five years and is 85 or over. ${recommendedOwner === 'partner' ? partnerName : clientName} is the older of the two, so in their name that drop comes sooner — less counted, sooner. And with the survivor option on, the earlier date is kept even if they pass away first. Nothing else changes: the income, the care fees and the couple assessment are the same whichever name it's in.`}
                                </JargonTerm>
                                . Change it if you like.
                            </p>
                        </div>
                    </>
                )}

                {/*
                  * ONE EVENT, TWO CONSEQUENCES, ONE BOX. These were two separate sections, and the
                  * owner read the pair as contradictory: "pay the highest income now" (the payments
                  * stop) sitting above "the full amount guaranteed to your family, whenever you die"
                  * (the capital returns). Both can be true at once — that combination is precisely
                  * what the market's aged care annuity is — but only if the screen shows them as two
                  * answers to the same question rather than two rival questions.
                  */}
                {(() => {
                    /*
                     * THE CHOICE, AND THE FINE CONTROL BEHIND IT.
                     *
                     * Three cards answer what a client actually weighs — the income, what the family
                     * gets, and what happens if the money is needed back. The slider is the same
                     * question again in percentage form, so it now sits behind a disclosure: the two
                     * ends are the only shapes providers quote, and the owner's note was that seeing
                     * the choice three times over (cards, then slider, then a sentence repeating
                     * both) is what made the page feel daunting.
                     */
                    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
                    const partnerAgeNow = isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;
                    const ownerAge = ownerKey === 'partner' ? partnerAgeNow : clientAge;
                    const otherAge = ownerKey === 'partner' ? clientAge : partnerAgeNow;
                    const ownerSex = ownerKey === 'partner' ? factFind?.partner?.sex : factFind?.client?.sex;
                    /*
                     * WHOSE DEATH PAYS THE LUMP SUM. Challenger's Liquid Lifetime PDS: "The
                     * applicable death benefit is only payable after the death of ALL PERSONS
                     * included as part of the investment." So on a reversionary or jointly owned
                     * annuity nothing is paid to the estate until BOTH have died — which is also why
                     * the capital access schedule runs on the longer of the two lives. The screen
                     * used to name the owner alone, which promised a couple a lump sum years before
                     * the product would pay one. `isCouple` guards it, because `continuation` keeps
                     * its default for a single client and must not make them read as a couple.
                     */
                    const jointLife = isCouple && (ownerKey === 'joint' || continuation !== 'highest');
                    const period = capitalAccessPeriodFor({
                        age: ownerAge,
                        sex: ownerSex,
                        partnerAge: otherAge,
                        partnerSex: ownerKey === 'partner' ? factFind?.client?.sex : factFind?.partner?.sex,
                        joint: jointLife,
                    });
                    const periodYears = period?.periodYears ?? null;
                    const whose = jointLife ? 'the survivor' : ownerName;
                    // Who has to die before the estate sees anything, in words for the card heading.
                    const lastToDie = jointLife ? `${ownerName} and ${otherName}` : null;
                    const isLifetime = annuityDetails.annuityType !== 'fixedTerm';

                    return (
                        <div className="space-y-3">
                            <p className="text-[11.5px] font-semibold text-slate-800">
                                {/*
                                  * "to your family" names the RECIPIENT, which the heading was
                                  * missing (owner, 27 Sep 2026). Without it the question could be
                                  * read as money coming back to the client, which is the other
                                  * column. Strictly the payment goes to the nominated beneficiaries
                                  * or the estate, and the detail under the cards says so; "your
                                  * family" is what a client recognises, so it leads.
                                  */}
                                {isCouple ? '2. What comes back to your family as a lump sum?' : 'What comes back to your family as a lump sum?'}
                            </p>
                            {isLifetime && (
                                <AnnuityShapeChoice
                                    purchaseAmount={purchaseAmount}
                                    age={ownerAge}
                                    sex={ownerSex}
                                    partnerAge={otherAge}
                                    joint={jointLife}
                                    indexation={!!annuityDetails.indexation}
                                    periodYears={periodYears}
                                    residualPercent={residualPercent}
                                    onChange={(pct) => setAnnuityDetails(prev => ({ ...prev, residualCapital: pct / 100 }))}
                                    ownerName={whose}
                                    bothLives={lastToDie}
                                    showGuaranteed={guaranteedShapeAvailable(factFind, ownerKey === 'joint' ? 'joint' : (ownerKey as 'client' | 'partner'))}
                                    wantsGuarantee={wantsGuarantee}
                                    onGuaranteeChange={setWantsGuarantee}
                                    onWantQuote={() => setQuoteOpen(true)}
                                />
                            )}

                            {/* Income only needs the client's tick before a plan with it is chosen (owner, 21 Oct 2026). */}
                            {onIncomeOnlyUnderstoodChange && isIncomeOnlyAnnuity(annuityDetails) && (
                                <IncomeOnlyTick factFind={factFind} amount={purchaseAmount} onChange={onIncomeOnlyUnderstoodChange} checked={incomeOnlyUnderstood} />
                            )}

                            {/*
                              * The guaranteed shape is the one we will not model on a guess. Its
                              * income is on the published table, but its means test turns on how the
                              * premium splits between the annuity and the life policy, which only the
                              * provider prices. So selecting it says so and sends the client to the
                              * quote fields rather than quietly producing a number.
                              */}
                            {isLifetime && wantsGuarantee && (
                                <div className="rounded-lg border border-slate-200 border-l-4 border-l-violet-500 bg-white px-3 py-2.5">
                                    <p className="text-[12px] font-semibold text-slate-800 mb-0.5">Modelled as an annuity and a life policy together</p>
                                    <p className="text-[11.5px] leading-relaxed text-slate-600">
                                        {guaranteedSlices
                                            /*
                                             * WHERE THE SPLIT COMES FROM IS NOT FOR THE CLIENT (owner,
                                             * 28 Sep 2026). We validated the structure against the
                                             * provider's own calculator, and the table is imported from
                                             * it, but that is our provenance, not the client's business,
                                             * and naming it on screen reads as an endorsement. The client
                                             * needs to know only that it is an estimate and that a quote
                                             * is required. The source stays in the code and the pack.
                                             */
                                            ? <>Of your {formatCurrency(purchaseAmount)}, about <strong>{formatCurrency(guaranteedSlices.annuity)}</strong> buys the annuity and <strong>{formatCurrency(guaranteedSlices.insurance)}</strong> buys the policy that tops up what the annuity would not return. That split is an estimate only and cannot be guaranteed{guaranteedSlices.clamped ? ' — your age is outside the range it covers, so the nearest age is used' : ''}. A provider quote is required.</>
                                            : <>The split between the annuity and the policy is an estimate only and cannot be guaranteed. A provider quote is required.</>}
                                    </p>
                                </div>
                            )}

                            {/*
                              * The slider asks the same question as the cards, as a percentage. It
                              * stays for anyone who wants a share in between, and for a fixed term
                              * where there are no cards, but it is folded away by default: providers
                              * quote the two ends, and showing the choice three times over is what
                              * made this screen feel long.
                              */}
                            {!isGuaranteedShape && (
                            <details open={!isLifetime}>
                                <summary className="text-[11.5px] font-medium text-blue-700 cursor-pointer list-none">
                                    {isLifetime ? 'Set an exact percentage instead' : 'How much comes back at the end of the term?'}
                                </summary>
                                <div className="mt-2">
                                    <label className="flex items-center gap-1 text-[12px] font-medium text-slate-700 mb-1">
                                        {isLifetime ? residualQuestion({ periodYears, ownerName: whose }) : 'How much of the purchase price comes back?'}
                                        <InfoIcon text={residualTooltip({ residualPct: residualPercent, periodYears })} />
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input type="range" min="0" max="100" value={residualPercent} onChange={handleResidualChange} className="flex-1 accent-blue-600" />
                                        <div className="w-16 shrink-0">
                                            <input type="number" min="0" max="100" value={residualPercent} onChange={handleResidualChange}
                                                className="w-full px-2 py-1.5 text-sm text-right border border-slate-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
                                        </div>
                                        <span className="text-sm text-slate-500">%</span>
                                    </div>
                                    {isLifetime && (
                                        <p className="mt-1.5 text-[11px] leading-relaxed text-slate-600">
                                            {capitalReturnedWords({ residualPct: residualPercent, periodYears, sexKnown: period?.sexKnown ?? null, ownerName: whose })}
                                        </p>
                                    )}
                                </div>
                            </details>
                            )}
                        </div>
                    );
                })()}

            </div>

            {/* 6. What this means for you */}
            {annuitySummary && (
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                    {/*
                      * IT HAS TO SAY WHICH SHAPE IT IS SHOWING. Owner on the live screen, 26 Sep
                      * 2026: clicking between "money back" and "the full amount guaranteed" left
                      * these figures on the money-back shape with nothing to say so. That is correct
                      * behaviour — the guaranteed shape cannot be modelled until a quote arrives —
                      * but silent correctness reads as a bug. The heading now names the shape every
                      * time, so the figures can never be read as belonging to a different card.
                      */}
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                            <p className="text-[12px] font-semibold text-slate-800">What this means for you</p>
                            <p className="text-[10.5px] text-slate-500 mt-0.5">
                                {type === 'fixedTerm'
                                    ? 'For the set-term annuity above.'
                                    : wantsGuarantee
                                        ? 'For “the full amount guaranteed to your family, whenever you die”.'
                                        : `For “${residualPercent > 0 ? 'money back if things change, or if you die early' : 'the highest income'}”.`}
                            </p>
                        </div>
                        {/*
                          * The estimate badge was grey 10px type in the corner, which is where a
                          * reader's eye goes last. It is the single most important caveat on the
                          * screen, so it is now a control: coloured, and it opens the provider
                          * section rather than leaving the client to find it.
                          */}
                        <button
                            type="button"
                            onClick={() => { setQuoteOpen(true); window.setTimeout(() => quoteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0); }}
                            className={`shrink-0 rounded-full border px-2.5 py-1 text-[10.5px] font-semibold ${
                                hasQuote
                                    ? 'border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50'
                                    : 'border-amber-300 bg-white text-amber-700 hover:bg-amber-50'
                            }`}
                        >
                            {hasQuote
                                ? <>From your provider&rsquo;s quote &mdash; <span className="underline">change it</span></>
                                : <>This is an estimate only &mdash; <span className="underline">get a quote</span> to confirm</>}
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 px-2.5 py-1.5">
                            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Guaranteed income</p>
                            <p className="text-base font-bold text-blue-700">{formatCurrency(annuitySummary.annualPayment)} <span className="text-[11px] font-medium text-slate-500">/yr</span></p>
                        </div>
                        <div className="rounded-lg border border-slate-200 border-l-4 border-l-emerald-500 px-2.5 py-1.5">
                            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Cuts assessed assets by</p>
                            <p className="text-base font-bold text-emerald-700">{formatCurrency(assetBenefit)}</p>
                        </div>
                    </div>
                    {assetBenefit > 0 ? (
                        <p className="text-[11.5px] text-slate-600">Centrelink counts only <strong>{formatCurrency(assessableAsset)}</strong> of your {formatCurrency(purchaseAmount)} — <strong>{formatCurrency(assetBenefit)} less</strong> than leaving it where it is, which can lift your Age Pension.</p>
                    ) : (
                        <p className="text-[11.5px] text-slate-600">This doesn’t change what Centrelink counts for you right now, but it still gives you a guaranteed income.</p>
                    )}
                    {/*
                      * ONE CENTRELINK EXPLANATION, NOT TWO. A second panel used to sit below this
                      * one (AnnuityFieldNote) repeating the 60%, the assets-test saving and the cost
                      * of giving up access. The owner's note, 26 Sep 2026: "surely we can have one
                      * section that covers both". The headline figures stay above; everything that
                      * was in the second panel — the income test, the step down at the threshold day
                      * and what the shape costs — is now behind this one disclosure.
                      */}
                    <details className="group/calc mt-2">
                        <summary className="text-[11.5px] font-medium text-blue-700 cursor-pointer list-none">How is this worked out?</summary>
                        <div className="mt-1.5 rounded-lg bg-white border border-slate-200 border-l-4 border-l-slate-300 px-3 py-2 text-[11px] leading-relaxed text-slate-600 space-y-1.5">
                            {isGuaranteedShape && guaranteedSlices ? (
                                <p>
                                    <strong>Counted as an asset:</strong> {formatCurrency(assessableAsset ?? 0)} — 60% of the {formatCurrency(guaranteedSlices.annuity)} annuity half, plus
                                    the whole {formatCurrency(guaranteedSlices.insurance)} policy half. Centrelink counts a life policy at the greater of what you paid and
                                    its cash-in value, and early on that is what you paid. That is why the guarantee buys far less assets-test relief than a plain annuity.
                                </p>
                            ) : (
                                <p><strong>Counted as an asset:</strong> {formatCurrency(assessableAsset ?? 0)}{type === 'lifetime' ? ' — 60% of what you put in.' : ' — this reduces over the term if you take back less than 100%.'}</p>
                            )}
                            <p><strong>Counted as income:</strong> {formatCurrency(annuitySummary.assessableIncome ?? 0)} a year{type === 'lifetime' ? ', which is 60% of the payments' : ''}. Both the Age Pension and the means-tested care fee follow.</p>
                            {type === 'lifetime' && (
                                <p><strong>It gets better later:</strong> the counted share {isGuaranteedShape ? 'of the annuity half ' : ''}drops from 60% to 30% at the threshold day — the later of age 85 and five years from now.</p>
                            )}
                            <p>
                                <strong>The cost:</strong>{' '}
                                {isGuaranteedShape
                                    ? 'the least income of the three shapes, and most of the money buys insurance rather than the annuity, so the assets-test saving is small. What you get for it is certainty: the full amount to your family whenever you die.'
                                    : residualPercent > 0
                                    ? 'a lower income, and the money stops being freely available — some can be taken back, falling each year to nothing.'
                                        : 'the capital is committed for life. There is no cash-out value and nothing comes back to the estate; that is what buys the higher income.'}
                            </p>
                            <p>Based on the current rules for annuities started on or after 1 July 2019.</p>
                        </div>
                    </details>
                </div>
            )}

            {/* 7. Warnings (engine) */}
            {(annuityWarnings?.length ?? 0) > 0 && (
                <div className="rounded-xl bg-white border border-slate-200 border-l-4 border-l-amber-500 px-3 py-2.5 text-xs text-slate-700 space-y-1">
                    {annuityWarnings?.map(warning => <div key={warning}>{warning}</div>)}
                </div>
            )}

            {/*
              * 8. THE QUOTE BLOCK IS WHAT THE AMBER BADGE OPENS. It used to sit here permanently,
              * headed "These figures are an estimate - get a quote when you're ready", directly
              * under a badge that already said "This is an estimate only - get a quote to confirm".
              * The owner: "we are again just duplicating text." The caveat is stated once, on the
              * badge beside the figures it qualifies; this is what appears when a client acts on it.
              * A quote already entered keeps the block open, so nothing a client typed can hide.
              */}
            {(quoteOpen || hasQuote) && (
                <div ref={quoteRef} className="rounded-xl border border-slate-200 border-l-4 border-l-amber-500 bg-white p-3">
                    <div className="flex items-start justify-between gap-3 mb-1">
                        <p className="text-[12px] font-semibold text-slate-800">A specialist provider can confirm the exact income</p>
                        {!hasQuote && (
                            <button type="button" onClick={() => setQuoteOpen(false)}
                                className="shrink-0 text-[11px] font-medium text-slate-500 hover:text-slate-700">Close</button>
                        )}
                    </div>
                    <p className="text-[11.5px] text-slate-600 mb-2">Enter their quote below and every figure above is re-modelled on their numbers instead of ours.</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {providerLinks.map(link => (
                            <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                                className="px-2.5 py-1 bg-white border border-slate-300 rounded-full text-[11px] text-blue-700 font-semibold hover:bg-slate-50 transition-colors">
                                Visit {link.name}
                            </a>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <CurrencyInput label="Annual income" value={annuityDetails.incomeOverride ?? ''}
                            onChange={(v) => setAnnuityDetails(prev => ({ ...prev, incomeOverride: v ?? null }))} placeholder="From quote" className="" />
                        <CurrencyInput label="Income counted" value={annuityDetails.assessableIncomeOverride ?? ''}
                            onChange={(v) => setAnnuityDetails(prev => ({ ...prev, assessableIncomeOverride: v ?? null }))} placeholder="Optional" className="" />
                        <CurrencyInput label="Asset counted" value={annuityDetails.assessableAssetOverride ?? ''}
                            onChange={(v) => setAnnuityDetails(prev => ({ ...prev, assessableAssetOverride: v ?? null }))} placeholder="Optional" className="" />
                    </div>
                </div>
            )}

            {/* 9. Risk callout — always visible, tight */}
            <div className="rounded-xl bg-white border border-slate-200 border-l-4 border-l-amber-500 px-3 py-2.5">
                <p className="text-[12px] font-semibold text-slate-800 mb-0.5 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-amber-600" /> Worth knowing before you commit</p>
                {/*
                  * THIS USED TO SAY "the money is locked in" WHATEVER THE CLIENT HAD CHOSEN.
                  * That is true only at nil residual. With capital returned there is a withdrawal
                  * value that falls in a straight line over the schedule period, so telling that
                  * client the money is locked away is simply wrong — and it is wrong in the
                  * direction that talks them out of the shape that suits them better.
                  */}
                <p className="text-[11.5px] text-slate-600">
                    {isGuaranteedShape
                        ? <>Your family gets the full amount back whenever you die, so the risk on that side is gone. What you give up is income — this shape pays the least of the three — and most of your money is buying insurance, so Centrelink still counts most of it. A cash-in value exists but the provider&rsquo;s quote states it.</>
                        : residualPercent <= 0
                            ? <>This shape pays the most, and it is the one that really is locked in: there is no cash-out value and nothing comes back to your family. People often live <strong>2–5 years</strong> in residential care, so passing away early can mean losing what is left in it.</>
                            : <>You can take money back, but not all of it and not forever — the most allowed falls each year to nothing, and cashing out ends the income. What the provider actually pays is usually less than that maximum.</>}
                    {annuityDetails.annuityType === 'fixedTerm' && <> With a <strong>set-term</strong> annuity the income also stops when the term ends.</>}
                    {isCouple && <> Keeping the payments going to your partner avoids the risk of an early death ending the income.</>}
                </p>
            </div>
        </div>
    );
};

export default AnnuityStrategyConfigurator;
